/* eslint-disable no-nested-ternary */
import {view} from '@sail/ui';
import {
  InlineDatumReactComponentProps,
  useAnalytics,
} from '@stripe-internal/inline-data';
import {LightCard} from '@stripe-internal/conversations-ui';

import {FormattedMessage, defineMessages} from 'react-intl';
import {useEffect} from 'react';
import {StatusSection} from './StatusSection';
import {CurrentMerchantSection} from '../helpers/CurrentMerchantSection';
import {PayoutDetailsSection} from './PayoutDetailsSection';
import {UpdatePayoutDetailsPermissionNotice} from './StatusUpdatePayoutDetails';

export type ExternalAccountDetails = {
  type: string;
  brand_icon?: string;
  destination: string;
  name: string;
  text: string;
  bank_phone_number?: string;
};

export type Payout = {
  amount: string;
  currency: string;
  destination?: ExternalAccountDetails;
  date?: number;
  status: string;
  trace_id?: string;
  trace_id_status?: string;
  token: string;
  failure_code?: string;
  failure_message?: string;
};

export type Audience = 'stripe' | 'express';

export type ValueProps = {
  payout?: Payout;
  has_payouts?: boolean;
  is_first_payout?: boolean;
  is_payout_date_over_5_days_ago?: boolean;
  has_update_payout_permissions?: boolean;
  has_view_payout_permissions?: boolean;
  merchant_name?: string;
  merchant_avatar?: string;
  merchant_token?: string;
  audience?: Audience;
  show_manual_payout_message?: boolean;
  universal_help_new_design?: boolean;
};

const messages = defineMessages({
  noViewPayoutPermissions: {
    id: 'mse_inline_data.merchant_payout_status.no_view_payout_permissions',
    description:
      'Message displayed when user does not have permission to view payouts.',
    defaultMessage:
      'You do not have permission to view payout details for this account. You can reach out to your administrator for details about your payouts, ',
  },
  noViewPayoutPermissionsLinkText: {
    id: 'mse_inline_data.merchant_payout_status.no_view_payout_permissions_link_text',
    description:
      'Second part of message displayed when user does not have permission to view payouts which links to Stripe Support article',
    defaultMessage: 'or view more information about Stripe user roles.',
  },
});

const noViewPayoutPermissionsLink =
  'https://docs.stripe.com/get-started/account/teams/roles';

export const MerchantPayoutStatus = (props: InlineDatumReactComponentProps) => {
  const {datum} = props;
  const value = datum.value as ValueProps;

  const {
    payout,
    has_payouts: hasPayouts,
    is_first_payout: isFirstPayout,
    is_payout_date_over_5_days_ago: isPayoutDateOver5DaysAgo,
    has_update_payout_permissions: updatePayoutPermissions,
    has_view_payout_permissions: viewPayoutPermissions,
    merchant_name: merchantName,
    merchant_avatar: merchantAvatar,
    merchant_token: merchantToken,
    show_manual_payout_message: showManualPayoutMessage,
    universal_help_new_design: universalHelpNewDesign,
    audience,
  } = value;

  const analytics = useAnalytics();
  useEffect(() => {
    analytics.viewed('uh_merchant_payout_status');
    if (!viewPayoutPermissions) {
      analytics.viewed('uh_merchant_payout_status_no_view_payout_permissions');
    }
  }, [analytics, viewPayoutPermissions]);

  let firstCard = <></>;
  let additionalCard = <></>;

  if (!viewPayoutPermissions) {
    firstCard = (
      <view.div
        data-testid="payout-no-view-permission-text"
        css={{font: 'label.medium'}}
      >
        <FormattedMessage {...messages.noViewPayoutPermissions} />
        <a
          href={noViewPayoutPermissionsLink}
          onClick={() => {
            analytics.action(
              'merchant_payout_status_no_view_payout_permissions_link_clicked',
              {
                url: noViewPayoutPermissionsLink,
              },
            );
          }}
          target="_blank"
          rel="noreferrer"
        >
          <FormattedMessage {...messages.noViewPayoutPermissionsLinkText} />
        </a>
      </view.div>
    );
  } else if (hasPayouts && payout) {
    firstCard = <PayoutDetailsSection payout={payout} audience={audience} />;
    additionalCard = (
      <>
        <LightCard isOuter>
          <view.div
            css={{stack: 'y', gap: 'medium'}}
            data-testid="merchant-payout-status-lightcard-2"
          >
            <StatusSection
              payout={payout}
              isFirstPayout={isFirstPayout}
              isPayoutDateOver5DaysAgo={isPayoutDateOver5DaysAgo}
              updatePayoutPermissions={updatePayoutPermissions}
              merchantToken={merchantToken}
              audience={audience}
              showManualPayoutMessage={showManualPayoutMessage}
              platformName={merchantName}
            />
          </view.div>
        </LightCard>
        <UpdatePayoutDetailsPermissionNotice
          payout={payout}
          updatePayoutPermissions={updatePayoutPermissions}
        />
      </>
    );
  } else {
    firstCard = (
      <StatusSection
        payout={null}
        isFirstPayout={isFirstPayout}
        isPayoutDateOver5DaysAgo={isPayoutDateOver5DaysAgo}
        updatePayoutPermissions={updatePayoutPermissions}
        audience={audience}
        showManualPayoutMessage={showManualPayoutMessage}
        platformName={merchantName}
      />
    );
  }

  return (
    <>
      <LightCard isOuter>
        <view.div
          css={{stack: 'y', gap: 'medium'}}
          data-testid="merchant-payout-status-lightcard-1"
        >
          {!universalHelpNewDesign && merchantName ? (
            <CurrentMerchantSection
              merchantName={merchantName}
              merchantAvatar={merchantAvatar}
            />
          ) : null}
          {firstCard}
        </view.div>
      </LightCard>
      {additionalCard}
    </>
  );
};
